import { useMemo, useState } from 'react'
import { AlertCircle, ListChecks, Loader2, Pencil, Plus, Power, Search } from 'lucide-react'
import { MesProductDialog } from '@/components/mes-products/MesProductDialog'
import { ProductCodeOptionSetsDialog } from '@/components/mes-products/ProductCodeOptionSetsDialog'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { useMesProductMutations, useMesProducts } from '@/hooks/useMesProducts'
import { useSortedRows } from '@/hooks/useSortedRows'
import { useTableColumns } from '@/hooks/useTableColumns'
import { matchesProduct } from '@/lib/mesProducts'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import type { MesProduct } from '@/types/cgt'

// Per-user column layout. The code identifies the row and Actions holds the
// controls, so both are locked — see src/components/table/index.ts.
const TABLE_ID = 'mes-product-codes'

const COLUMNS: TableColumnDef[] = [
  { id: 'code', header: 'Product Code', locked: true, defaultWidth: 150, sortable: true },
  { id: 'description1', header: 'Product Description 1', defaultWidth: 260, sortable: true },
  { id: 'description2', header: 'Product Description 2', defaultWidth: 260, sortable: true },
  { id: 'productType', header: 'Product Type', defaultWidth: 220, sortable: true },
  { id: 'clinicalOrCommercial', header: 'Clinical or Commercial', defaultWidth: 170, sortable: true },
  { id: 'active', header: 'Active', defaultWidth: 100, sortable: true },
  // 2 icon buttons at 40px + 4px gap + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 120 },
]

const SORT: SortAccessors<MesProduct> = {
  code: p => p.code,
  description1: p => p.description1,
  description2: p => p.description2,
  productType: p => p.productType,
  clinicalOrCommercial: p => p.clinicalOrCommercial,
  active: p => (p.active ? 'Active' : 'Inactive'),
}

function Text({ value }: { value: string }) {
  return (
    <TableCell>
      <div className='truncate' title={value || undefined}>
        {value || <span className='text-muted-foreground'>—</span>}
      </div>
    </TableCell>
  )
}

/**
 * Configure → Product Code (z8nrz7fvhx): the products an MES order can make,
 * each with the spec's 24 fields. Not the ISBT 128 list beside it (Products
 * CGT). Readable with product_codes.read; adding, editing and switching one off
 * need product_codes.manage. A Product Code is never deleted: an MES order that
 * names it keeps it. Option Sets here are the selection fields' own lists,
 * apart from the forms' Option Sets tab.
 */
export function ProductCodesSettings() {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.productCodesManage)
  const cols = useTableColumns(TABLE_ID, COLUMNS)

  const list = useMesProducts()
  const { update } = useMesProductMutations()

  const [q, setQ] = useState('')
  const all = list.data
  const filtered = useMemo(() => (all ?? []).filter(p => matchesProduct(p, q)), [all, q])
  const rows = useSortedRows(filtered, cols.sort, SORT)

  // `undefined`: closed. `null`: adding. A string: that product.
  const [open, setOpen] = useState<string | null | undefined>(undefined)
  const [toggleError, setToggleError] = useState<string | null>(null)
  const [listsOpen, setListsOpen] = useState(false)

  function toggleActive(p: MesProduct) {
    setToggleError(null)
    update.mutate(
      { id: p.id, payload: { active: !p.active } },
      { onError: e => setToggleError(e instanceof Error ? e.message : 'Could not change the product.') },
    )
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-base font-semibold text-foreground'>Product Code</h2>
        <p className='text-sm text-muted-foreground'>
          The products an MES order can make, picked in Create MES order. The tenant&apos;s own list, separate from
          the ISBT 128 codes under Products CGT. An inactive product is no longer offered for a new order.
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative min-w-[240px] max-w-sm flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by code or description…'
            aria-label='Search product codes'
            value={q}
            onChange={e => setQ(e.target.value)}
            className='pl-9'
          />
        </div>
        <div className='ml-auto flex items-center gap-2'>
          <Button variant='outline' onClick={() => setListsOpen(true)}>
            <ListChecks className='mr-2 h-4 w-4' />
            Option Sets
          </Button>
          {canManage && (
            <Button onClick={() => setOpen(null)}>
              <Plus className='mr-2 h-4 w-4' />
              Add Product Code
            </Button>
          )}
        </div>
      </div>

      {toggleError && (
        <div className='flex items-center gap-2 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          {toggleError}
        </div>
      )}

      <Card>
        <CardContent className='p-0'>
          {list.isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {list.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load product codes. {list.error.message}
            </div>
          )}

          {all && rows.length === 0 && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              {all.length > 0
                ? 'No product codes match your search.'
                : canManage
                  ? 'No product codes yet. Add one to get started.'
                  : 'No product codes yet.'}
            </div>
          )}

          {all && rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(p => (
                  <TableRow key={p.id} className={p.active ? undefined : 'text-muted-foreground'}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        code: (
                          <TableCell className='font-medium'>
                            <button
                              type='button'
                              className='text-primary underline-offset-2 hover:underline'
                              onClick={() => setOpen(p.id)}
                            >
                              {p.code}
                            </button>
                          </TableCell>
                        ),
                        description1: <Text value={p.description1} />,
                        description2: <Text value={p.description2} />,
                        productType: <Text value={p.productType} />,
                        clinicalOrCommercial: <Text value={p.clinicalOrCommercial} />,
                        active: (
                          <TableCell>
                            {p.active ? (
                              <span className='inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700'>
                                Active
                              </span>
                            ) : (
                              <span className='inline-flex rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground'>
                                Inactive
                              </span>
                            )}
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <div className='flex items-center justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='icon'
                                title={canManage ? 'Edit product code' : 'Open product code'}
                                onClick={() => setOpen(p.id)}
                              >
                                <Pencil className='h-4 w-4' />
                              </Button>
                              {canManage && (
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title={p.active ? 'Deactivate' : 'Reactivate'}
                                  disabled={update.isPending}
                                  onClick={() => toggleActive(p)}
                                >
                                  <Power className={p.active ? 'h-4 w-4 text-destructive' : 'h-4 w-4 text-emerald-600'} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MesProductDialog
        open={open !== undefined}
        onOpenChange={o => !o && setOpen(undefined)}
        productId={open ?? null}
        canEdit={canManage}
      />

      <ProductCodeOptionSetsDialog open={listsOpen} onOpenChange={setListsOpen} canEdit={canManage} />
    </div>
  )
}
